import { ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { Rostering } from "./pages/Rostering";
import { Employees } from "./pages/Employees";
import { Training } from "./pages/Training";
import { LeaveAndAvailability } from "./pages/LeaveAndAvailability";
import { Participants } from "./pages/Participants";
import { Referrals } from "./pages/Referrals";
import { WebsiteInbox } from "./pages/crm/WebsiteInbox";
import { Enquiries } from "./pages/crm/Enquiries";
import { ApplicantsQueue } from "./pages/crm/ApplicantsQueue";
import { QualityRegisters } from "./pages/crm/QualityRegisters";
import { Cases } from "./pages/Cases";
import { Incidents } from "./pages/Incidents";
import { Policies } from "./pages/Policies";
import { SiteTasks } from "./pages/SiteTasks";
import { Updates } from "./pages/Updates";
import { TimeClock } from "./pages/TimeClock";
import { Providers } from "./pages/Providers";
import { ProviderProfile } from "./pages/ProviderProfile";
import { Settings } from "./pages/Settings";
import { AccessDenied } from "./pages/AccessDenied";
import { CrmDashboard } from "./pages/crm/CrmDashboard";
import { Pipeline } from "./pages/crm/Pipeline";
import { Quotes } from "./pages/crm/Quotes";
import { NdisPricing } from "./pages/crm/NdisPricing";
import { Analytics } from "./pages/crm/Analytics";
import { ReportBuilder } from "./pages/crm/ReportBuilder";
import { Caseload } from "./pages/crm/Caseload";
import { Compliance } from "./pages/crm/Compliance";
import { ComplianceDashboard } from "./pages/crm/ComplianceDashboard";
import { Staff } from "./pages/crm/Staff";
import { AskQuestion } from "./pages/AskQuestion";
import { PartnerClients } from "./pages/PartnerClients";
import { PartnerRequests } from "./pages/PartnerRequests";
import { PartnerMessages } from "./pages/PartnerMessages";
import { PartnerOrganisation } from "./pages/PartnerOrganisation";
import { Invoices } from "./pages/crm/Invoices";
import { Allocations } from "./pages/crm/Allocations";
import { Timesheets } from "./pages/crm/Timesheets";
import { Finance } from "./pages/crm/Finance";
import { MyProfile } from "./pages/MyProfile";
import { InviteAccept } from "./pages/InviteAccept";
import { ForgotPassword } from "./pages/ForgotPassword";
import { Support } from "./pages/Support";
import { OnboardingWizard } from "./pages/OnboardingWizard";
import { StaffOnboarding } from "./pages/StaffOnboarding";
import { Compliance as DocumentExpiry } from "./pages/Compliance";
import { SmartGroups } from "./pages/SmartGroups";
import { Reports } from "./pages/Reports";
import { OrgChart } from "./pages/OrgChart";
import { Forms } from "./pages/Forms";
import { ProgressNotes } from "./pages/ProgressNotes";
import { Chat } from "./pages/Chat";
import { ProviderPortalPreview } from "./pages/ProviderPortalPreview";

const Protected = () => {
    const { user, loading } = useAuth();

    if (loading) {
        return <div className="login"><p style={{ color: "var(--muted)" }}>Loading…</p></div>;
    }
    return user ? <Layout /> : <Navigate to="/login" replace />;
};

/**
 * Surface gate. A staff login that types /crm/pipeline into the address bar
 * gets the access-declined page with a request button — not a blank screen,
 * and not the data (the API refuses that independently).
 */
const CrmOnly = ({ children }: { children: ReactNode }) => {
    const { mayAccess } = useAuth();
    return mayAccess("crm") ? <>{children}</> : <AccessDenied surface="crm" />;
};

const ShiftflowOnly = ({ children }: { children: ReactNode }) => {
    const { mayAccess } = useAuth();
    return mayAccess("shiftflow") ? <>{children}</> : <AccessDenied surface="shiftflow" />;
};

/**
 * Partners land on their portal, support lands on the account-helpdesk page
 * (neither surface applies to them), everyone else on the ShiftFlow dashboard.
 */
const Home = () => {
    const { user } = useAuth();
    // A gated worker cannot proceed past onboarding — the API is locked too, so
    // this redirect just keeps the UI honest.
    if (user?.onboardingRequired) return <Navigate to="/onboarding" replace />;
    if (user?.role === "partner") return <Navigate to="/partner" replace />;
    if (user?.role === "support") return <Navigate to="/support" replace />;
    return <ShiftflowOnly><Dashboard /></ShiftflowOnly>;
};

export const App = () => (
    <AuthProvider>
        <BrowserRouter>
            <Routes>
                <Route path="/login" element={<Login />} />
                {/* Public: the invite landing page must work signed-out. */}
                <Route path="/invite/:token" element={<InviteAccept />} />
                {/* Public: requesting a reset happens before any session exists. */}
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route element={<Protected />}>
                    {/* Forced onboarding — not surface-gated, so a gated worker can reach it. */}
                    <Route path="/onboarding" element={<OnboardingWizard />} />
                    {/* Admin onboarding board (role-gated by nav + API). */}
                    <Route path="/onboarding-admin" element={<ShiftflowOnly><StaffOnboarding /></ShiftflowOnly>} />
                    {/* Compliance / document expiry (role-gated by nav + API). */}
                    <Route path="/compliance" element={<ShiftflowOnly><DocumentExpiry /></ShiftflowOnly>} />
                    {/* ShiftFlow surface */}
                    <Route path="/" element={<Home />} />
                    <Route path="/rostering" element={<ShiftflowOnly><Rostering /></ShiftflowOnly>} />
                    <Route path="/time-clock" element={<ShiftflowOnly><TimeClock /></ShiftflowOnly>} />
                    <Route path="/forms" element={<ShiftflowOnly><Forms /></ShiftflowOnly>} />
                    <Route path="/updates" element={<ShiftflowOnly><Updates /></ShiftflowOnly>} />
                    <Route path="/chat" element={<ShiftflowOnly><Chat /></ShiftflowOnly>} />
                    <Route path="/employees" element={<ShiftflowOnly><Employees /></ShiftflowOnly>} />
                    <Route path="/smart-groups" element={<ShiftflowOnly><SmartGroups /></ShiftflowOnly>} />
                    <Route path="/org-chart" element={<ShiftflowOnly><OrgChart /></ShiftflowOnly>} />
                    <Route path="/reports" element={<ShiftflowOnly><Reports /></ShiftflowOnly>} />
                    <Route path="/training" element={<ShiftflowOnly><Training /></ShiftflowOnly>} />
                    <Route path="/leave" element={<ShiftflowOnly><LeaveAndAvailability /></ShiftflowOnly>} />
                    <Route path="/participants" element={<ShiftflowOnly><Participants /></ShiftflowOnly>} />
                    <Route path="/progress-notes" element={<ShiftflowOnly><ProgressNotes /></ShiftflowOnly>} />
                    <Route path="/provider-portal" element={<ShiftflowOnly><ProviderPortalPreview /></ShiftflowOnly>} />
                    <Route path="/site-tasks" element={<ShiftflowOnly><SiteTasks /></ShiftflowOnly>} />
                    <Route path="/my-pay" element={<ShiftflowOnly><Timesheets mineOnly /></ShiftflowOnly>} />
                    {/* Personal, not a surface module: every signed-in account —
                        partners included — owns a profile and a password. */}
                    <Route path="/my-profile" element={<MyProfile />} />

                    {/* Shared governance — reachable from both surfaces */}
                    <Route path="/referrals" element={<Referrals />} />
                    <Route path="/website-inbox" element={<WebsiteInbox />} />
                    <Route path="/enquiry" element={<Enquiries />} />
                    <Route path="/applicants" element={<ApplicantsQueue />} />
                    <Route path="/quality" element={<QualityRegisters />} />
                    <Route path="/cases" element={<Cases />} />
                    <Route path="/providers" element={<Providers />} />
                    <Route path="/providers/:id" element={<ProviderProfile />} />
                    <Route path="/incidents" element={<Incidents />} />
                    <Route path="/policies" element={<Policies />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/help" element={<AskQuestion />} />
                    <Route path="/support" element={<Support />} />
                    <Route path="/partner" element={<PartnerClients />} />
                    <Route path="/partner/requests" element={<PartnerRequests />} />
                    <Route path="/partner/invoices" element={<Invoices />} />
                    <Route path="/partner/policies" element={<Policies />} />
                    <Route path="/partner/organisation" element={<PartnerOrganisation />} />
                    <Route path="/partner/messages" element={<PartnerMessages />} />
                    <Route path="/crm/partner-requests" element={<CrmOnly><PartnerRequests /></CrmOnly>} />

                    {/* CRM surface */}
                    <Route path="/crm" element={<CrmOnly><CrmDashboard /></CrmOnly>} />
                    <Route path="/crm/pipeline" element={<CrmOnly><Pipeline /></CrmOnly>} />
                    {/* The old Inquiries address stays alive: same page, business tab. */}
                    <Route path="/crm/inquiries" element={<CrmOnly><Enquiries initial="business" /></CrmOnly>} />
                    <Route path="/crm/quotes" element={<CrmOnly><Quotes /></CrmOnly>} />
                    <Route path="/crm/ndis-pricing" element={<CrmOnly><NdisPricing /></CrmOnly>} />
                    <Route path="/crm/analytics" element={<CrmOnly><Analytics /></CrmOnly>} />
                    <Route path="/crm/reports" element={<CrmOnly><ReportBuilder /></CrmOnly>} />
                    <Route path="/crm/caseload" element={<CrmOnly><Caseload /></CrmOnly>} />
                    <Route path="/crm/compliance" element={<CrmOnly><Compliance /></CrmOnly>} />
                    <Route path="/crm/compliance-dashboard" element={<CrmOnly><ComplianceDashboard /></CrmOnly>} />
                    <Route path="/crm/staff" element={<CrmOnly><Staff /></CrmOnly>} />
                    <Route path="/crm/invoices" element={<CrmOnly><Invoices /></CrmOnly>} />
                    <Route path="/crm/allocations" element={<CrmOnly><Allocations /></CrmOnly>} />
                    <Route path="/crm/timesheets" element={<CrmOnly><Timesheets /></CrmOnly>} />
                    <Route path="/crm/finance" element={<CrmOnly><Finance /></CrmOnly>} />
                    {/* Companies and contacts share the Providers module — same
                        tables, same records, locked to one tab each. The key
                        matters: without it React keeps the same component
                        instance when navigating between the two routes, and the
                        initial-tab state from the first visit sticks. */}
                    <Route path="/crm/companies" element={<CrmOnly><Providers key="organisations" only="organisations" /></CrmOnly>} />
                    <Route path="/crm/contacts" element={<CrmOnly><Providers key="contacts" only="contacts" /></CrmOnly>} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </BrowserRouter>
    </AuthProvider>
);
